import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminShell from './AdminShell';
import {
  getChatConversations,
  getLandlordChatMessages,
  sendLandlordMessage,
  markChatRead,
} from '../../api/client';

// Notification sound (short beep using Web Audio API)
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    gain.gain.value = 0.3;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

// Request browser push permission
function requestPushPermission() {
  if ('Notification' in window && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

function showPushNotification(title, body) {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      new Notification(title, { body, icon: '/icons/icon-192.png', tag: 'chat-msg' });
    } catch {}
  }
}

export default function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const prevUnreadRef = useRef(0);

  // Request push permission on mount
  useEffect(() => { requestPushPermission(); }, []);

  // Load conversations + check for new messages
  const loadConversations = useCallback(async () => {
    try {
      const data = await getChatConversations();
      const convs = Array.isArray(data) ? data : [];
      setConversations(convs);

      // Check total unread
      const totalUnread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      if (totalUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        // New message arrived
        playNotificationSound();
        const newConv = convs.find(c => c.unread_count > 0);
        if (newConv && document.hidden) {
          showPushNotification(
            `Сообщение от ${newConv.guest_name || 'гостя'}`,
            newConv.last_message || 'Новое сообщение'
          );
        }
      }
      prevUnreadRef.current = totalUnread;
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 5000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // When active session changes
  useEffect(() => {
    if (!activeSession) return;

    async function loadMessages() {
      try {
        const data = await getLandlordChatMessages(activeSession);
        setMessages(data.messages || []);
        await markChatRead(activeSession);
        loadConversations();
      } catch {}
    }

    loadMessages();
    pollRef.current = setInterval(async () => {
      try {
        const data = await getLandlordChatMessages(activeSession);
        setMessages(data.messages || []);
      } catch {}
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [activeSession, loadConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Update page title with unread count
  useEffect(() => {
    const total = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
    document.title = total > 0 ? `(${total}) Чат — Bystrov` : 'Чат — Bystrov';
    return () => { document.title = 'Bystrov'; };
  }, [conversations]);

  async function handleSend(e) {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const msg = await sendLandlordMessage(activeSession, input.trim());
      setMessages(prev => [...prev, msg]);
      setInput('');
    } catch { alert('Ошибка отправки'); }
    finally { setSending(false); }
  }

  function selectConversation(sessionId) {
    setActiveSession(sessionId);
    setMobileShowChat(true);
  }

  function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  }

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  return (
    <AdminShell title={`Чат${totalUnread > 0 ? ` (${totalUnread})` : ''}`}>
      <div className="admin-chat-wrapper">
        {/* Conversations list */}
        <div className={`admin-chat-sidebar ${mobileShowChat ? 'admin-chat-hidden-mobile' : ''}`}>
          <div className="admin-chat-sidebar-header">
            Диалоги
            {totalUnread > 0 && <span className="admin-chat-total-badge">{totalUnread}</span>}
          </div>
          {loading && <div className="admin-chat-empty">Загрузка...</div>}
          {!loading && conversations.length === 0 && (
            <div className="admin-chat-empty">Нет сообщений</div>
          )}
          {conversations.map(c => (
            <div
              key={c.session_id}
              className={`admin-chat-conv ${activeSession === c.session_id ? 'active' : ''}`}
              onClick={() => selectConversation(c.session_id)}
            >
              <div className="admin-chat-conv-top">
                <span className="admin-chat-conv-name">{c.guest_name || 'Гость'}</span>
                <span className="admin-chat-conv-time">{formatTime(c.last_message_at)}</span>
              </div>
              <div className="admin-chat-conv-bottom">
                <span className="admin-chat-conv-preview">{c.last_message || '...'}</span>
                {c.unread_count > 0 && (
                  <span className="admin-chat-unread-badge">{c.unread_count}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Chat window */}
        <div className={`admin-chat-main ${!mobileShowChat ? 'admin-chat-hidden-mobile' : ''}`}>
          {!activeSession ? (
            <div className="admin-chat-placeholder">
              <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
              <div>Выберите диалог</div>
            </div>
          ) : (
            <>
              {/* Mobile back button */}
              <div className="admin-chat-msg-header">
                <button className="admin-chat-back-btn" onClick={() => setMobileShowChat(false)}>
                  ← Назад
                </button>
                <span className="admin-chat-msg-title">
                  {conversations.find(c => c.session_id === activeSession)?.guest_name || 'Гость'}
                </span>
              </div>

              <div className="admin-chat-messages">
                {messages.map(msg => (
                  <div key={msg.id} className={`admin-chat-msg ${msg.direction === 'landlord' ? 'mine' : 'theirs'}`}>
                    <div className={`admin-chat-bubble ${msg.direction === 'landlord' ? 'mine' : 'theirs'}`}>
                      {msg.text}
                      <div className="admin-chat-msg-time">
                        {new Date(msg.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              <form className="admin-chat-input-bar" onSubmit={handleSend}>
                <input
                  type="text"
                  className="admin-chat-input"
                  placeholder="Введите ответ..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  disabled={sending}
                />
                <button type="submit" className="admin-chat-send-btn" disabled={!input.trim() || sending}>
                  ➤
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </AdminShell>
  );
}